import {throwError as observableThrowError,  Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { tap, catchError, map } from 'rxjs/operators';
import {JsonService} from "../../services/core/json.service";
import {CustomerApiResources} from "./customer.constants";
import {
  CustomerPdfModuleSummary,
  CustomerPdfTemplateDto,
  CustomerPdfTemplatePayload,
  CustomerPdfTemplateSaveResult,
} from "./interfaces/customer-pdf-template.interface";

@Injectable({
  providedIn: 'root'
})
export class CustomerDataService {
  private baseURL;

  constructor(
    private http: HttpClient,
    private jsonService: JsonService,
  ) {
    this.baseURL = CustomerApiResources;
  }

  list(model: any = null): Observable<any> {
    const endPoint = `${this.baseURL.list}`;
    return this.http.post<any>(endPoint, model)
                    .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  search(model: any = null): Observable<any> {
    const endPoint = `${this.baseURL.search}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  create(model: any): Observable<any> {
    const endPoint = `${this.baseURL.create}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  copyCustomer(id: any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.copy}/${id}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  /**
   * The customer's default PDF template for a module, or null when unset.
   *
   * Unwraps the HttpResponse envelope ({data:{success,data}}) so callers never
   * touch response.data.data.
   */
  getCustomerPdfTemplate(
    customerId: number | string,
    moduleId: number
  ): Observable<CustomerPdfTemplateDto | null> {
    const endPoint = `${this.baseURL.customerPdfTemplate}`;
    return this.http.get<any>(endPoint, { params: { customer_id: customerId, module_id: moduleId } })
      .pipe(
        tap(data => (this.jsonService.doStringify(data))),
        map(response => (response?.data?.data ?? null) as CustomerPdfTemplateDto | null),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * One-call summary: every supported module and the customer's default template
   * (id + name) for each. Same endpoint as getCustomerPdfTemplate, but with no
   * module_id, so the dialog populates its whole left panel in a single request.
   */
  getCustomerPdfTemplatesSummary(
    customerId: number | string
  ): Observable<CustomerPdfModuleSummary[]> {
    const endPoint = `${this.baseURL.customerPdfTemplate}`;
    return this.http.get<any>(endPoint, { params: { customer_id: customerId } })
      .pipe(
        tap(data => (this.jsonService.doStringify(data))),
        map(response => (response?.data?.data?.modules ?? []) as CustomerPdfModuleSummary[]),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Create, update, or clear the customer's default PDF template for a module.
   * A null pdf_template_id in the payload clears the default.
   *
   * Unwraps the envelope and returns the resulting mapping (null when cleared)
   * alongside the backend's success message.
   */
  saveCustomerPdfTemplate(
    model: CustomerPdfTemplatePayload
  ): Observable<CustomerPdfTemplateSaveResult> {
    const endPoint = `${this.baseURL.customerPdfTemplate}`;
    return this.http.post<any>(endPoint, model)
      .pipe(
        tap(data => (this.jsonService.doStringify(data))),
        map(response => ({
          mapping: (response?.data?.data ?? null) as CustomerPdfTemplateDto | null,
          message: response?.data?.message ?? '',
        })),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Resolve which template (out of an already-loaded template list) the customer
   * has saved as their default for a given module.
   *
   * Shared by every Export-to-PDF dialog so the summary lookup + module matching
   * lives in exactly one place. Returns null (so the caller keeps the office/
   * system default) when:
   *   - no customerId is supplied, or
   *   - the customer has no saved default for the module, or
   *   - that saved template is not present in the loaded list.
   *
   * moduleKey is matched case-insensitively against the summary's `slug` and
   * `module_name`, so callers may pass either a slug ('Change Order') or a
   * lowercase module name ('bid', 'submittal').
   */
  resolveCustomerDefaultTemplate(
    customerId: number | string | null | undefined,
    moduleKey: string,
    templates: any[]
  ): Observable<any | null> {
    const key = (moduleKey ?? '').toString().trim().toLowerCase();
    if (!customerId || !key || !Array.isArray(templates) || templates.length === 0) {
      return of(null);
    }
    return this.getCustomerPdfTemplatesSummary(customerId).pipe(
      map((modules: CustomerPdfModuleSummary[]) => {
        const match = (modules ?? []).find((m) =>
          (m?.slug ?? '').toString().trim().toLowerCase() === key ||
          (m?.module_name ?? '').toString().trim().toLowerCase() === key
        );
        const tplId = match?.pdf_template_id;
        if (tplId === null || tplId === undefined) {
          return null;
        }
        return templates.find((t: any) => t?.id == tplId) ?? null;
      }),
      catchError(() => of(null))
    );
  }

  detail(id: any, params?: any): Observable<any> {
    const endPoint = `${this.baseURL.detail}/${id}`;
    return this.http.get<any>(endPoint, { params })
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  update(id:any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.update}/${id}`;
    return this.http.patch<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  delete(id:any): Observable<any> {
    const endPoint = `${this.baseURL.delete}/${id}`;
    return this.http.delete<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  // Bulk delete: DELETE customers/delete/multiple with { customer_ids: [...] }
  deleteMultiple(customerIds: any[]): Observable<any> {
    const endPoint = `${this.baseURL.deleteMultiple}`;
    return this.http.request<any>('delete', endPoint, { body: { customer_ids: customerIds } })
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  // Pre-check which of the selected customers can be deleted (and why not, for the rest)
  deleteAvailability(customerIds: any[]): Observable<any> {
    const endPoint = `${this.baseURL.deleteAvailability}`;
    return this.http.post<any>(endPoint, { customer_ids: customerIds })
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  onHold(id:any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.onhold}/${id}`;
    return this.http.patch<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  onChangeStatus(id:any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.changeStatus}/${id}`;
    return this.http.patch<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  import(model: any): Observable<any> {
    const endPoint = `${this.baseURL.import}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  // Import History Methods
  getImportHistory(params: any = {}): Observable<any> {
    const endPoint = `${this.baseURL.importHistory}`;
    let httpParams = new HttpParams();
    httpParams.set('loadingService', 'false');
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        httpParams = httpParams.set(key, params[key]);
      }
    });
    return this.http.get<any>(endPoint, { params: httpParams })
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  getImportHistoryDetails(id: number): Observable<any> {
    const endPoint = `${this.baseURL.importHistory}/${id}`;
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  deleteImportHistory(id: number): Observable<any> {
    const endPoint = `${this.baseURL.importHistory}/${id}`;
    return this.http.delete<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  getImportStats(params: any = {}): Observable<any> {
    const endPoint = `${this.baseURL.importHistory}/stats`;
    let httpParams = new HttpParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        httpParams = httpParams.set(key, params[key]);
      }
    });
    return this.http.get<any>(endPoint, { params: httpParams })
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  // Enhanced Import Methods
  downloadSampleFile(): Observable<any> {
    const endPoint = `${this.baseURL.downloadSample}`;
    return this.http.get(endPoint, { responseType: 'blob' })
      .pipe(catchError(this.errorHandlerMessage));
  }

  uploadImportFile(file: File): Observable<any> {
    const endPoint = `${this.baseURL.uploadImport}`;
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(endPoint, formData)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  validateImportData(data: any): Observable<any> {
    const endPoint = `${this.baseURL.validateImport}`;
    return this.http.post<any>(endPoint, data)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  executeImport(data: any): Observable<any> {
    const endPoint = `${this.baseURL.executeImport}`;
    return this.http.post<any>(endPoint, data)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  // Customer Site Information Methods
  getCustomerSites(customerId: number): Observable<any> {
    const endPoint = `${this.baseURL.customerSites}/${customerId}/sites`;
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  createCustomerSite(data: any): Observable<any> {
    const endPoint = `${this.baseURL.createCustomerSite}`;
    return this.http.post<any>(endPoint, data)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  // Jobsite Requirements Methods
  getJobsiteRequirements(): Observable<any> {
    const endPoint = `${this.baseURL.jobsiteRequirements}`;
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  // Jobsite Access Methods
  getJobsiteAccesses(params: any = {}): Observable<any> {
    const endPoint = `${this.baseURL.jobsiteAccesses}`;
    let httpParams = new HttpParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        httpParams = httpParams.set(key, params[key]);
      }
    });
    return this.http.get<any>(endPoint, { params: httpParams })
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  createJobsiteAccess(data: any): Observable<any> {
    const endPoint = `${this.baseURL.createJobsiteAccess}`;
    return this.http.post<any>(endPoint, data)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  // Jobsite Parking Methods
  getJobsiteParking(params: any = {}): Observable<any> {
    const endPoint = `${this.baseURL.jobsiteParking}`;
    let httpParams = new HttpParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        httpParams = httpParams.set(key, params[key]);
      }
    });
    return this.http.get<any>(endPoint, { params: httpParams })
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  createJobsiteParking(data: any): Observable<any> {
    const endPoint = `${this.baseURL.createJobsiteParking}`;
    return this.http.post<any>(endPoint, data)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  errorHandler(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }

  errorHandlerMessage(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }
}